<script setup lang="ts">
import type { FallbackCategory, GameSearchApiFilter } from '@game/types'
import { computed, fallbackListSlug, useGameListTracking, useGames, useI18n } from '#imports'

const props = withDefaults(defineProps<{
    category: FallbackCategory
    pageSize?: number
    hasSidebar?: boolean
    lazy?: boolean
}>(), {
    pageSize: 30,
    hasSidebar: false,
    lazy: true,
})

// Plain game searches, keyed by category. JACKPOT and LIVE_CASINO need bespoke
// fetch/sort logic and delegate to their own components (see template).
const SEARCH_CONFIG: Partial<Record<FallbackCategory, { filter: GameSearchApiFilter, titleKey: string }>> = {
    SLOTS: { filter: { game_types: ['SLOTS'] }, titleKey: 'SLOTS' },
    CASINO: { filter: { game_types: ['CASINO'] }, titleKey: 'CASINO' },
    BINGO: { filter: { game_types: ['BINGO'] }, titleKey: 'BINGO' },
    POPULAR: { filter: {}, titleKey: 'POPULAR' },
    OTHER: { filter: { game_types: ['SLOTS'], has_jackpot: false }, titleKey: 'OTHER' },
}

const { t } = useI18n()
// Getter, not a snapshot: the first fallback section's category follows a prop.
const { trackingListSlug, trackingOrigin, onGameClick } = useGameListTracking('gametile', () => fallbackListSlug(props.category))

const search = computed(() => SEARCH_CONFIG[props.category])
const title = computed(() => t(`game.gameList.${search.value?.titleKey ?? props.category}`))

const { data, isLoading } = useGames({
    params: { page: 1, page_size: props.pageSize },
    filter: () => search.value?.filter ?? {},
    lazy: props.lazy,
    enabled: () => !!search.value,
    key: () => `game-list-fallback:${props.category}:${props.pageSize}`,
})
</script>

<template>
    <GameListFallbackJackpot v-if="category === 'JACKPOT'" :lazy="lazy" :has-sidebar="hasSidebar" />
    <GameListFallbackLiveCasino v-else-if="category === 'LIVE_CASINO'" :lazy="lazy" :has-sidebar="hasSidebar" />
    <div v-else-if="isLoading || data?.data?.length" class="custom-game-section">
        <GameList
            :category="category"
            :tracking-list-slug="trackingListSlug"
            :tracking-origin="trackingOrigin"
            :games="data?.data || []"
            :title="title"
            :has-sidebar="hasSidebar"
            :skeletons="8"
            :video-enabled="true"
            :trim-dangling-tiles="true"
            @game-click="onGameClick"
        />
    </div>
</template>
